// Libraries
import React from 'react';

// Supermove
import DataLoader from '@supermove/components/src/DataLoader';
import {gql, useQuery} from '@supermove/graphql';
import {useModal, useNavigationDOM, useToast} from '@supermove/hooks';
import {CalendarIntegrationModel, OrganizationModel, UserModel} from '@supermove/models';

// App
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import CalendarIntegrationForm from '@shared/modules/AccountCalendar/forms/CalendarIntegrationForm';
import useUpdateCalendarIntegrationMutation from '@shared/modules/AccountCalendar/hooks/useUpdateCalendarIntegrationMutation';
import CalendarIntegrationPage from 'modules/Organization/Settings/Communication/components/CalendarIntegrationPage';
import QuiteEditingCalendarIntegrationModal from 'modules/Organization/Settings/Communication/components/QuiteEditingCalendarIntegrationModal';

const EditCalendarIntegrationPageContent = ({
  organization,
  calendarIntegration,
}: {
  organization: OrganizationModel;
  calendarIntegration: CalendarIntegrationModel;
}) => {
  const {navigator} = useNavigationDOM();
  const confirmQuiteEditingModal = useModal({name: 'Confirm Quite Editing Modal'});
  const updateDocumentFlowSuccessToast = useToast({
    ToastComponent: SuccessToast,
  });

  const field = 'calendarIntegrationForm';

  const calendarIntegrationForm = CalendarIntegrationForm.edit(calendarIntegration);

  const {form, submitting, handleSubmit} = useUpdateCalendarIntegrationMutation({
    calendarIntegrationForm,
    onSuccess: (calendarIntegration) => {
      updateDocumentFlowSuccessToast.handleToast({
        message: `Calendar integration updated!`,
      });
      navigator.replace(`/settings/communication/calendar-integrations`);
    },
    onError: (errors: any) => console.log({errors}),
  });

  const goToIntegrationsPage = () => {
    navigator.replace(`/settings/communication/calendar-integrations`);
  };

  return (
    <React.Fragment>
      <QuiteEditingCalendarIntegrationModal
        calendarIntegration={calendarIntegration}
        isOpen={confirmQuiteEditingModal.isOpen}
        handleSubmit={goToIntegrationsPage}
        handleClose={confirmQuiteEditingModal.handleClose}
      />
      <CalendarIntegrationPage
        organization={organization}
        calendarIntegration={calendarIntegration}
        form={form}
        field={field}
        submitting={submitting}
        handleSubmit={handleSubmit}
        handleCancel={confirmQuiteEditingModal.handleOpen}
      />
    </React.Fragment>
  );
};

const EditCalendarIntegrationPage = () => {
  const {params} = useNavigationDOM();
  const {data, loading} = useQuery<{viewer: UserModel}>(EditCalendarIntegrationPage.query, {
    variables: {
      uuid: params.calendarIntegrationUuid,
    },
    fetchPolicy: 'network-only',
  });

  return (
    <DataLoader data={data} loading={loading}>
      {({loadedData}) => (
        <EditCalendarIntegrationPageContent
          organization={loadedData.viewer.viewingOrganization}
          calendarIntegration={loadedData.viewer.viewingOrganization.calendarIntegration}
        />
      )}
    </DataLoader>
  );
};

EditCalendarIntegrationPage.query = gql`
  ${CalendarIntegrationPage.fragment}
  ${CalendarIntegrationPage.fragment_edit}
  ${CalendarIntegrationForm.edit.fragment}

  query EditCalendarIntegrationPage($uuid: String!) {
    ${gql.query}
    viewer {
      id
      viewingOrganization {
        id
        calendarIntegration(uuid: $uuid) {
          id
          ...CalendarIntegrationForm_edit
        }
        ...CalendarIntegrationPage
        ...CalendarIntegrationPage_edit
      }
    }
  }
`;

export default EditCalendarIntegrationPage;
